import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./ApproveCandidates.css";
import { Document, Page, pdfjs } from "react-pdf";
import { X } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { renderAsync } from "docx-preview";
import toast from "react-hot-toast";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ActiveUsers = () => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cvType, setCvType] = useState(null); // 'pdf' or 'docx'
  const [currentBlob, setCurrentBlob] = useState(null);
  const docxRef = useRef(null);

  useEffect(() => {
    fetchActiveUsers();
  }, []);

  useEffect(() => {
    if (showPdfViewer && cvType === "docx" && currentBlob && docxRef.current) {
      renderAsync(currentBlob, docxRef.current).catch((err) => {
        console.error("Error rendering DOCX:", err);
        toast.error("Failed to render DOCX preview");
      });
    }
  }, [showPdfViewer, cvType, currentBlob]);

  const fetchActiveUsers = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"}/api/admin/active-users`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch active users");
      }

      const data = await response.json();
      setActiveUsers(data);
    } catch (error) {
      setError("Failed to fetch active users");
      console.error("Error fetching active users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCV = async (userId, filename) => {
    try {
      setPdfLoading(true);
      setShowPdfViewer(true);
      setPageNumber(1);
      setCvType(null);

      const token = localStorage.getItem("adminToken");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3500";
      const fullUrl = `${apiUrl}/api/admin/candidates/${userId}/download-cv`;

      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        setCurrentBlob(blob);

        const isPdf =
          blob.type.includes("pdf") || filename?.toLowerCase().endsWith(".pdf");
        const isDocx =
          blob.type.includes("word") ||
          blob.type.includes("officedocument.wordprocessingml.document") ||
          filename?.toLowerCase().endsWith(".docx");

        if (isPdf) {
          setCvType("pdf");
          const url = window.URL.createObjectURL(blob);
          setPdfUrl(url);
        } else if (isDocx) {
          setCvType("docx");
          const url = window.URL.createObjectURL(blob);
          setPdfUrl(url);
        } else {
          console.error("Unsupported file type:", blob.type);
          toast.error("Unsupported file type. Expected PDF or DOCX.");
          setShowPdfViewer(false);
          setPdfLoading(false);
          return;
        }
      } else {
        console.error("Failed to load CV");
        toast.error("Failed to load CV");
        setShowPdfViewer(false);
      }
    } catch (error) {
      console.error("Error loading CV:", error);
      toast.error("Error loading CV");
      setShowPdfViewer(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setCurrentBlob(null);
    setCvType(null);
    setNumPages(null);
    setPageNumber(1);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="loading-spinner">Loading active users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="page-title">Active Users</h1>
          <div className="view-toggle" role="tablist" aria-label="View mode">
            <button
              type="button"
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              role="tab"
              aria-selected={viewMode === "table"}
            >
              Table
            </button>
            <button
              type="button"
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              role="tab"
              aria-selected={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {activeUsers.length === 0 ? (
          <div className="empty-state">
            <h3>No active users found</h3>
            <p>There are currently no active users in the system.</p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Type</th>
                      <th>Created</th>
                      <th>Resume/Website</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>
                                {u.user_type === "corporate"
                                  ? (u.company_name || "C")[0]
                                  : u.first_name?.[0] || "U"}
                              </span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {u.user_type === "corporate"
                                  ? u.company_name || "Unnamed Company"
                                  : `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                                    "Unnamed User"}
                              </div>
                              <div className="email">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${u.user_type === "corporate" ? "corporate" : "candidate"}`}
                          >
                            {u.user_type === "corporate"
                              ? "Corporate"
                              : "Candidate"}
                          </span>
                        </td>
                        <td>
                          {new Date(u.created_at).toLocaleDateString()}{" "}
                          <span className="muted">
                            {new Date(u.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </td>
                        <td>
                          {u.user_type === "candidate" ? (
                            u.cv_filename ? (
                              <button
                                className="link-action"
                                onClick={() =>
                                  handleViewCV(u.user_id, u.cv_filename)
                                }
                              >
                                {u.cv_filename}
                              </button>
                            ) : (
                              <span className="muted">No CV</span>
                            )
                          ) : u.website ? (
                            <a
                              className="link-action"
                              href={u.website}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Visit
                            </a>
                          ) : (
                            <span className="muted">No site</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {activeUsers.map((user) => (
                  <div key={user.id} className="candidate-card">
                    <div className="candidate-header">
                      <div
                        className={`candidate-avatar ${user.user_type === "corporate" ? "corporate" : ""}`}
                      >
                        {user.user_type === "corporate" ? (
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M3 21h18"></path>
                            <path d="M3 7h18"></path>
                            <path d="M3 3h18"></path>
                            <path d="M7 21V7"></path>
                            <path d="M17 21V7"></path>
                          </svg>
                        ) : (
                          <span>{user.first_name?.charAt(0) || "U"}</span>
                        )}
                      </div>
                      <div className="candidate-info">
                        <h3>
                          {user.user_type === "corporate"
                            ? user.company_name || "Unnamed Company"
                            : `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                              "Unnamed User"}
                        </h3>
                        <p>{user.email}</p>
                      </div>
                      <div className={`status-badge ${user.user_type}`}>
                        {user.user_type === "corporate"
                          ? "Corporate"
                          : "Candidate"}
                      </div>
                    </div>

                    <div className="candidate-details">
                      <div className="detail-item">
                        <strong>User Type:</strong> {user.user_type}
                      </div>
                      <div className="detail-item">
                        <strong>Review Status:</strong> {user.review_status}
                      </div>
                      <div className="detail-item">
                        <strong>Created:</strong>{" "}
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      {user.user_type === "candidate" && (
                        <>
                          <div className="detail-item">
                            <strong>Resume:</strong>
                            {user.cv_filename ? (
                              <button
                                className="download-link"
                                onClick={() =>
                                  handleViewCV(user.user_id, user.cv_filename)
                                }
                              >
                                {user.cv_filename}
                              </button>
                            ) : (
                              <span className="not-specified">
                                Not specified
                              </span>
                            )}
                          </div>
                          <div className="detail-item">
                            <strong>LinkedIn:</strong>
                            {user.linkedin_url ? (
                              <a
                                href={user.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {user.linkedin_url}
                              </a>
                            ) : (
                              <span className="not-specified">
                                Not specified
                              </span>
                            )}
                          </div>
                        </>
                      )}
                      {user.user_type === "corporate" && (
                        <>
                          {user.industry && (
                            <div className="detail-item">
                              <strong>Industry:</strong> {user.industry}
                            </div>
                          )}
                          {user.company_size && (
                            <div className="detail-item">
                              <strong>Company Size:</strong> {user.company_size}
                            </div>
                          )}
                          {user.website && (
                            <div className="detail-item">
                              <strong>Website:</strong>
                              <a
                                href={user.website}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {user.website}
                              </a>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* CV Preview Modal */}
      {showPdfViewer && (
        <div className="dialog-overlay" style={{ zIndex: 1000 }}>
          <div
            className="dialog-content"
            style={{ maxWidth: "90vw", width: "900px", maxHeight: "90vh" }}
          >
            <div className="dialog-header">
              <h2>Candidate CV</h2>
              <button className="modal-close" onClick={handleClosePdfViewer}>
                <X size={24} />
              </button>
            </div>

            <div
              className="dialog-body"
              style={{
                padding: "1rem",
                maxHeight: "calc(90vh - 150px)",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {pdfLoading ? (
                <div className="loading">Loading CV...</div>
              ) : cvType === "pdf" && pdfUrl ? (
                <>
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => {
                      console.error("Error loading PDF:", error);
                      toast.error("Failed to load PDF");
                    }}
                    loading={<div className="loading">Loading PDF...</div>}
                  >
                    <Page
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={Math.min(850, window.innerWidth * 0.85)}
                    />
                  </Document>

                  {numPages && numPages > 1 && (
                    <div
                      style={{
                        marginTop: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        padding: "0.5rem",
                        background: "#f5f5f5",
                        borderRadius: "4px",
                      }}
                    >
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        style={{ minWidth: "80px" }}
                      >
                        Previous
                      </button>
                      <span style={{ fontWeight: "500" }}>
                        Page {pageNumber} of {numPages}
                      </span>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                        style={{ minWidth: "80px" }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : cvType === "docx" ? (
                <div
                  ref={docxRef}
                  style={{
                    width: "100%",
                    minHeight: "500px",
                    backgroundColor: "#fff",
                  }}
                />
              ) : (
                <div className="error">Failed to load CV</div>
              )}
            </div>

            <div className="dialog-footer">
              <button
                className="btn btn-primary"
                onClick={handleClosePdfViewer}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveUsers;
